import { useCallback, useEffect, useMemo, useState } from 'react';

import { useTheme } from '@chakra-ui/react';
import styled from '@emotion/styled';
import { useQueryClient } from '@tanstack/react-query';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { capitalize, startCase } from 'lodash';
import moment from 'moment';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { CustomHeaderCell } from 'screens/Team/Rabbit/Goals/AgGridTreeView/components/CustomHeaderCell';

import { isMobileView } from 'constants/isMobileView';
import { localStorageKeys } from 'constants/localStorageKeys';

import { showTaskDetails } from 'services/taskDetails';

import { AgGridWrapper } from 'uiToolkitV1/AgGridWrapper';
import { TableBodyRow } from 'uiToolkitV1/TableComponents';

import {
  DueDate,
  InitiativesNameComponent,
  KebabTableMenuInitiatives,
  ParentPostComponent,
  SimpleComponent,
} from './AGGridComponents';

export const StyledInitiativeTableBodyRow = styled(TableBodyRow)(({ theme }) => ({
  '& .action': {
    display: 'none',
  },
  '&:hover': {
    cursor: 'pointer',
    '& .info': {
      display: 'none',
    },
    '& .action': {
      display: 'flex',
    },
    'td:first-of-type p': {
      color: theme.colors.text['1565BC'],
    },
  },
}));

export const getFlexJustifyFromCellAlign = (align) => {
  switch (align) {
    case 'left':
      return 'flex-start';
    case 'center':
      return 'center';
    case 'right':
      return 'flex-end';
    default:
      return 'center';
  }
};

const defaultRowsPerPage = 10;

export const defaultRowsPerPageLSObject = {
  [localStorageKeys.rabbitInitiativeTableRowsPerPage]: defaultRowsPerPage,
};

function AGGridTable({ tableData, filtersArray, gridStatePersistenceKey = null, gridRef }) {
  const dispatch = useDispatch();
  const history = useHistory();

  const initiativeFilterParams = {
    filters: [
      {
        filter: 'agSetColumnFilter',
        display: 'accordion',
        title: 'Filter by Status',
        applyMiniFilterWhileTyping: true,
        filterParams: {
          values: filtersArray.filter((val) => val.value !== 'ALL').map((filter) => filter.value),
          valueFormatter: ({ value }) => capitalize(startCase(value)),
          getValue: (data) => data.data.status,
        },
      },

      {
        filterParams: {
          filterOptions: ['contains', 'notContains'],
          getValue: (data) => data.data.title,
          textFormatter: (r) => {
            if (r == null) return null;

            return r
              .toLowerCase()
              .replace(/[àáâãäå]/g, 'a')
              .replace(/æ/g, 'ae')
              .replace(/ç/g, 'c')
              .replace(/[èéêë]/g, 'e')
              .replace(/[ìíîï]/g, 'i')
              .replace(/ñ/g, 'n')
              .replace(/[òóôõö]/g, 'o')
              .replace(/œ/g, 'oe')
              .replace(/[ùúûü]/g, 'u')
              .replace(/[ýÿ]/g, 'y');
          },
          debounceMs: 200,
          maxNumConditions: 1,
          suppressAndOrCondition: true,
        },
      },
    ],
  };

  const [rowData, setRowData] = useState();

  const showDetails = (e, taskId, type) => {
    if (e) e.stopPropagation();
    dispatch(
      showTaskDetails({
        taskIds: [taskId],
        onDelete: () => {},
        onUpdate: (task) => {},
        employee: {},
        type,
        history,
      })
    );

    //  sendTaskEvent({ pageType, eventName: TASK_EVENTS.openedSidePanel, referrer: 'MeshList' });
  };

  const handleViewDetails = ({ event, data, column }) => {
    if (column.colId !== 'kebab-menu') {
      const { uuid, type } = data;
      showDetails(event, uuid, type);
    }
  };

  const [columnDefs, setColumnDefs] = useState([
    {
      colId: 'title',
      headerName: 'Initiatives',
      field: 'title',
      filter: 'agMultiColumnFilter',
      filterParams: initiativeFilterParams,
      sortable: true,
      comparator: (valueA, valueB) => {
        if (valueA.title === valueB.title) return 0;
        return valueA.title > valueB.title ? 1 : -1;
      },
      minWidth: 350,
      valueGetter: (data) => ({ title: data.data?.title, isCompleted: data.data?.completionTS !== null }),
      cellRenderer: InitiativesNameComponent,
      getQuickFilterText: (params) => params.data.title,
      // maxWidth: 450
    },
    {
      colId: 'parentPosts',
      headerName: 'Parent',
      field: 'parentPosts',
      cellRenderer: ParentPostComponent,
      minWidth: 160,
      filter: true,
      filterParams: {
        // copy pasta from ag grid.
        // not touching it for now
        title: 'Filter by Parent',
        applyMiniFilterWhileTyping: true,
        getValue: (data) => data.data.parentDetails?.title,

        textFormatter: (r) => {
          if (r === null) return null;
          return r
            .toLowerCase()
            .replace(/[àáâãäå]/g, 'a')
            .replace(/æ/g, 'ae')
            .replace(/ç/g, 'c')
            .replace(/[èéêë]/g, 'e')
            .replace(/[ìíîï]/g, 'i')
            .replace(/ñ/g, 'n')
            .replace(/[òóôõö]/g, 'o')
            .replace(/œ/g, 'oe')
            .replace(/[ùúûü]/g, 'u')
            .replace(/[ýÿ]/g, 'y');
        },
        filterOptions: ['contains', 'notContains'],
        debounceMs: 200,
        suppressAndOrCondition: true,
      },
      // maxWidth: 280
    },
    {
      colId: 'toIds',
      headerName: 'Owners',
      field: 'toIds',
      cellRenderer: SimpleComponent,
      minWidth: 160,
      filter: true,
      sortable: true,
      filterParams: {
        // copy pasta from ag grid.
        // not touching it for now
        title: 'Filter by Owner',
        getValue: (data) => data.data?.toIds.map((owner) => owner.displayName),
        textFormatter: (r) => {
          if (r === null) return null;
          return r
            .toLowerCase()
            .replace(/[àáâãäå]/g, 'a')
            .replace(/æ/g, 'ae')
            .replace(/ç/g, 'c')
            .replace(/[èéêë]/g, 'e')
            .replace(/[ìíîï]/g, 'i')
            .replace(/ñ/g, 'n')
            .replace(/[òóôõö]/g, 'o')
            .replace(/œ/g, 'oe')
            .replace(/[ùúûü]/g, 'u')
            .replace(/[ýÿ]/g, 'y');
        },
        filterOptions: ['contains', 'notContains'],
        debounceMs: 200,
        suppressAndOrCondition: true,
      },
      // maxWidth: 280
    },
    {
      colId: 'dueTime',
      headerName: 'Due',
      field: 'dueTime',
      cellRenderer: DueDate,

      minWidth: 300,
      filter: 'agDateColumnFilter',
      filterParams: {
        // comparator: dateComparator,
        applyMiniFilterWhileTyping: true,
        suppressAndOrCondition: true,
        filterOptions: ['equals'],
        comparator: (filterLocalDateAtMidnight, cellValue) => {
          const dateAsString = cellValue;
          if (dateAsString == null) return -1;
          const dateParts = dateAsString.split('/');
          const cellDate = new Date(Number(dateParts[2]), Number(dateParts[1]) - 1, Number(dateParts[0]));

          if (filterLocalDateAtMidnight.getTime() === cellDate.getTime()) {
            return 0;
          }

          if (cellDate < filterLocalDateAtMidnight) {
            return -1;
          }

          if (cellDate > filterLocalDateAtMidnight) {
            return 1;
          }
          return 0;
        },
        getValue: (data) => {
          const originalDate = new Date(data.data?.dueTime?.timeStamp);
          return moment(originalDate).format('DD/MM/YYYY');
        },
        inRangeFloatingFilterDateFormat: 'Do MMM YYYY',
        // pass in additional parameters to the Date Filter
      },
    },
    {
      colId: 'kebab-menu',
      field: '',
      maxWidth: 75,
      headerName: '',
      pinned: 'right',
      lockPosition: 'right',
      suppressHeaderMenuButton: true,
      cellClass: 'locked-col',
      cellRenderer: KebabTableMenuInitiatives,
      valueGetter: (data) => {
        const accessPrivilegesList = Array.isArray(data.data?.accessPrivileges)
          ? data.data?.accessPrivileges.map(({ name }) => name)
          : [];
        return accessPrivilegesList;
      },
      suppressColumnsToolPanel: true,
    },
  ]);

  const columnDefsResponsive = [
    {
      headerName: 'Initiatives',
      colId: 'title',
      field: 'title',
      filter: 'agMultiColumnFilter',
      filterParams: initiativeFilterParams,
      menuTabs: [],
      flex: 1,
      valueGetter: (data) => ({ title: data.data?.title, isCompleted: data.data?.completionTS !== null }),
      minWidth: 255,
      cellRenderer: InitiativesNameComponent,
      getQuickFilterText: (params) => params.data.title,
    },
    {
      colId: 'kebab-menu',
      field: '',
      maxWidth: 85,
      flex: 1,
      headerName: '',
      pinned: 'right',
      // lockPosition: 'right',
      cellClass: 'locked-col',
      suppressHeaderMenuButton: true,
      cellRenderer: KebabTableMenuInitiatives,
      valueGetter: (data) => {
        const accessPrivilegesList = Array.isArray(data.data?.accessPrivileges)
          ? data.data?.accessPrivileges.map(({ name }) => name)
          : [];
        return accessPrivilegesList;
      },
    },
  ];

  const autoGroupColumnDefResponsive = {
    headerName: 'Initiatives',
    colId: 'title',
    field: 'title',
    minWidth: 250,
    flex: 1,
    cellStyle: { height: 'auto' },
    wrapText: true,
    autoHeight: true,
    menuTabs: [],
  };

  useEffect(() => {
    setRowData(tableData);
  }, [tableData]);

  const defaultColDef = useMemo(
    () => ({
      flex: 1,
      menuTabs: ['filterMenuTab'],
      sortable: true,
      headerComponent: CustomHeaderCell,
    }),
    []
  );

  const getRowStyle = (params) => ({ cursor: 'pointer' });

  const getRowId = useCallback(({ data }) => data?.uuid, []);

  return (
    <div className="ag-theme-alpine" style={{ width: '100%' }}>
      <AgGridWrapper
        key={gridStatePersistenceKey}
        gridStatePersistenceKey={gridStatePersistenceKey}
        columnDefs={isMobileView() ? columnDefsResponsive : columnDefs}
        rowData={rowData}
        paginationPageSize={10}
        ref={gridRef}
        defaultColDef={defaultColDef}
        pagination
        suppressPaginationPanel={rowData?.length < 6 || rowData?.length === undefined || rowData?.length === 0}
        domLayout="autoHeight"
        animateRows
        onCellClicked={handleViewDetails}
        getRowStyle={getRowStyle}
        rowHeight={60}
        getRowId={getRowId}
        autoGroupColumnDef={isMobileView() && autoGroupColumnDefResponsive}
        /* In Mobile view, we have only one column , so pinning it left is creating empty space in center */
        additionalMobileProps={{ pinned: undefined, lockPosition: undefined }}
        isMobileView={isMobileView()}
      />
    </div>
  );
}

export default AGGridTable;
